/**
 * The partner record.
 *
 * Same shape as the ambassador editor — grouped by what you would be doing when
 * you open it — and the same stylesheet, because they are the same kind of
 * screen rather than two that happen to look alike.
 */
import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import {
  PARTNER_KINDS,
  PARTNER_STATUSES,
  type Partner,
} from '@/types/partners'
import '@/shared/ui/RecordForm.css'
import { NotesField } from '@/shared/notes/NotesField'

interface Props {
  partner: Partner
  isNew: boolean
  onClose: () => void
  onSave: (draft: Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>, id?: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

export function PartnerModal({ partner, isNew, onClose, onSave, onDelete }: Props) {
  const [form, setForm] = useState<Partner>(partner)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = (patch: Partial<Partner>) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    if (!form.name.trim()) {
      setError('A partner needs a name.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const { id: _id, createdAt: _c, updatedAt: _u, ...draft } = form
      void _id
      void _c
      void _u
      await onSave(
        {
          ...draft,
          name: draft.name.trim(),
          website: draft.website.trim(),
          instagram: draft.instagram.trim().replace(/^@/, ''),
          facebook: draft.facebook.trim().replace(/^@/, ''),
        },
        isNew ? undefined : partner.id,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={isNew ? 'New partner' : partner.name}
      onClose={onClose}
      width={820}
      footer={
        <>
          {!isNew && (
            <button
              className="btn btn--ghost recform__delete"
              disabled={saving}
              onClick={() => void onDelete(partner.id)}
            >
              Delete partner
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="recform">
        <section className="recform__group">
          <h4 className="recform__legend">Who they are</h4>
          <div className="recform__grid">
            <label className="field">
              <span>Name</span>
              <input
                className="input"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Kind</span>
              <select
                className="input"
                value={form.kind}
                onChange={(e) => set({ kind: e.target.value as Partner['kind'] })}
              >
                {PARTNER_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Status</span>
              <select
                className="input"
                value={form.status}
                onChange={(e) => set({ status: e.target.value as Partner['status'] })}
              >
                {PARTNER_STATUSES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Country</span>
              <input
                className="input"
                value={form.country}
                onChange={(e) => set({ country: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Instagram</span>
              <input
                className="input"
                value={form.instagram}
                onChange={(e) => set({ instagram: e.target.value })}
                placeholder="without the @"
              />
            </label>
            <label className="field">
              <span>Facebook</span>
              <input
                className="input"
                value={form.facebook}
                onChange={(e) => set({ facebook: e.target.value })}
              />
            </label>
            <label className="field recform__wide">
              <span>Website</span>
              <input
                className="input"
                value={form.website}
                onChange={(e) => set({ website: e.target.value })}
                placeholder="https://…"
              />
            </label>
          </div>
        </section>

        <section className="recform__group">
          <h4 className="recform__legend">Who we deal with</h4>
          <div className="recform__grid">
            <label className="field">
              <span>Contact person</span>
              <input
                className="input"
                value={form.contactName}
                onChange={(e) => set({ contactName: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Role</span>
              <input
                className="input"
                value={form.contactRole}
                onChange={(e) => set({ contactRole: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                className="input"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Phone</span>
              <input
                className="input"
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
              />
            </label>
          </div>
        </section>

        <section className="recform__group">
          <h4 className="recform__legend">The arrangement</h4>
          <div className="recform__grid">
            <label className="field recform__wide">
              <span>What it is</span>
              <textarea
                className="input"
                rows={3}
                value={form.arrangement}
                onChange={(e) => set({ arrangement: e.target.value })}
                placeholder="What each side does, and what either gets out of it."
              />
            </label>
            <label className="field">
              <span>Campaign code</span>
              <input
                className="input"
                value={form.campaignCode}
                onChange={(e) => set({ campaignCode: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Owner</span>
              <input
                className="input"
                value={form.owner}
                onChange={(e) => set({ owner: e.target.value })}
              />
            </label>
          </div>
          <p className="recform__note">
            A campaign code here should match a real code in the product’s promo collections.
            Nothing writes to those from this screen — that has to go through a server-side
            function, and it crosses the project boundary.
          </p>
        </section>

        {/*
          AVV-2026-002, and decision 91.

          This replaces a single `notes` textarea. Marketing's report was
          *"vanskelig å legge til nye notater om partner eller dialoger med
          partner"* — and that is exactly what one shared string is: to add to it
          you retype what is there, two people overwrite each other, and nothing
          says who wrote what or when. The old text is carried into the log as
          its first entry rather than discarded (91).
        */}
        <NotesField
          kind="partner"
          recordId={isNew ? '' : form.id}
          legend="Notes & dialogue"
          prose={form.notes}
        />

        {error && <div className="recform__error">{error}</div>}
      </div>
    </Modal>
  )
}
